'use strict';

/**
 * Soracom Authentication Coverage Management Module
 * Handles coverage selection, storage, and UI updates
 */
window.SoracomAuthCoverage = (() => {

  // Coverage storage key
  const COVERAGE_KEY = 'soracom_coverage_type';

  // UI timing constants
  const COVERAGE_PANEL_CLOSE_DELAY = 150; // ms
  const COVERAGE_STORE_DELAY = 300; // defer heavy auth store

  /**
   * Get stored coverage from localStorage
   * @returns {string|null} Coverage type or null
   */
  function getStoredCoverage() {
    try {
      return localStorage.getItem(COVERAGE_KEY) || null;
    } catch (_) {
      return null;
    }
  }

  /**
   * Set coverage in localStorage
   * @param {string} cov - Coverage type to store
   */
  function setStoredCoverage(cov) {
    try {
      if (cov) localStorage.setItem(COVERAGE_KEY, cov);
    } catch (_) {
      /* ignore quota errors */
    }
  }

  /**
   * Clear coverage from localStorage
   */
  function clearStoredCoverage() {
    try {
      localStorage.removeItem(COVERAGE_KEY);
    } catch (_) {
      /* ignore */
    }
  }

  /**
   * Get coverage types from JWT token
   * @param {string} token - JWT token
   * @returns {Array} Array of coverage types
   */
  function getCoverageTypes(token) {
    // Enhanced JWT validation
    if (!window.SoracomAuthUtils?.validateJWTStructure?.(token)) {
      window.SoracomAuthUtils?.productionSafeLog?.('warn', 'Invalid JWT token structure');
      return ['g', 'jp'];
    }

    try {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload.coverage) {
          const coverageTypes = Array.isArray(payload.coverage) ? payload.coverage : [payload.coverage];

          // Validate coverage types are expected values
          const validCoverageTypes = coverageTypes.filter(type => ['g', 'jp'].includes(type));
          if (validCoverageTypes.length > 0) {
            return validCoverageTypes;
          }
        }
      }
    } catch (e) {
      window.SoracomAuthUtils?.productionSafeLog?.('warn', 'Could not decode token:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]');
    }

    return ['g', 'jp'];
  }

  /**
   * Update coverage UI display
   * @param {string} coverageType - Coverage type ('g' or 'jp')
   */
  function updateCoverageUI(coverageType) {
    // Refresh DOM cache to ensure we get current elements
    window.SoracomAuthUtils?.DOM_CACHE.invalidate();

    const coverageLabel = window.SoracomAuthUtils?.DOM_CACHE.getCoverageLabel();
    const coverageCheckmark = window.SoracomAuthUtils?.DOM_CACHE.getCoverageCheckmark();

    if (coverageLabel) {
      const labelText = coverageType === 'g' ? 'Global' : 'Japan';
      const hostText = coverageType === 'g' ? 'g.api.soracom.io' : 'jp.api.soracom.io';

      coverageLabel.textContent = labelText + ' ';
      const hostSpan = document.createElement('span');
      hostSpan.className = 'coverage-host';
      hostSpan.textContent = `(${hostText})`;
      coverageLabel.appendChild(hostSpan);
    }

    // The checkmark visibility is now handled by CSS when the coverage option has 'selected' class
    // No need to manually show/hide it with inline styles

    const globalOption = document.getElementById('coverage-g');
    const japanOption = document.getElementById('coverage-jp');
    if (!globalOption || !japanOption) {
      return;
    }

    // Clear all coverage options first
    globalOption.classList.remove('active', 'selected');
    japanOption.classList.remove('active', 'selected');

    // Set active state for selected coverage (using only 'selected' class)
    if (coverageType === 'g') {
      globalOption.classList.add('selected');
    } else if (coverageType === 'jp') {
      japanOption.classList.add('selected');
    }

    const authPanel = window.SoracomAuthUtils?.DOM_CACHE.getAuthPanel();
    if (authPanel) {
      if (coverageType) {
        authPanel.classList.add('coverage-selected');
      } else {
        authPanel.classList.remove('coverage-selected');
      }
    } else {
      // Fallback: try to find the auth panel directly
      const directAuthPanel = document.getElementById('sandbox-authentication');
      if (directAuthPanel) {
        if (coverageType) {
          directAuthPanel.classList.add('coverage-selected');
        } else {
          directAuthPanel.classList.remove('coverage-selected');
        }
      }
    }
  }

  /**
   * Ensure coverage panel is open
   */
  function ensureCoveragePanelOpen() {
    if (window.signingOut) return;

    if (window.coveragePanelOpening) return;

    const authData = window.SoracomAuth ? window.SoracomAuth.getAuthState() : null;
    if (!authData) return;
    if (authData.coverage) return;

    const panel = document.getElementById('coverage-panel');
    if (panel && panel.classList.contains('open')) return;

    const openPanel = () => {
      const panel = document.getElementById('coverage-panel');
      if (!panel) return false;
      const menu = document.getElementById('coverage-menu');

      panel.classList.add('open');
      if (menu) {
        menu.classList.add('open');
        menu.classList.add('active');
      }

      watchCoveragePanelUntilChosen();
      return true;
    };

    if (openPanel()) return;

    const domObserver = new MutationObserver(() => {
      if (openPanel()) {
        domObserver.disconnect();
      }
    });
    domObserver.observe(document.body, { childList: true, subtree: true });
  }

  /**
   * Watch coverage panel until coverage is chosen
   */
  function watchCoveragePanelUntilChosen() {
    // Set up coverage selection without forcing panel to stay open
    const authData = window.SoracomAuth ? window.SoracomAuth.getAuthState() : null;
    if (!authData) return;
    if (authData.coverage) return;

    // Enable coverage selection
    try {
      window.SoracomAuth?.enableCoverageSelection();
    } catch (e) {
      window.SoracomAuthUtils?.productionSafeLog?.('warn', 'enableCoverageSelection failed:', window.SoracomAuthUtils?.sanitizeForLogging?.(e?.message || e) || '[error details unavailable]');
      const panel = document.getElementById('coverage-panel');
      const menu = document.getElementById('coverage-menu');
      if (panel) panel.classList.remove('open');
      if (menu) {
        menu.classList.remove('open');
        menu.classList.remove('active');
      }
    }
  }

  // Public API
  return {
    getStoredCoverage,
    setStoredCoverage,
    clearStoredCoverage,
    getCoverageTypes,
    updateCoverageUI,
    ensureCoveragePanelOpen,
    watchCoveragePanelUntilChosen,
    COVERAGE_PANEL_CLOSE_DELAY,
    COVERAGE_STORE_DELAY
  };

})();
